import { useCallback, useMemo } from 'react';
import type { Href } from 'expo-router';
import {
  defineMessages,
  type IntlShape,
  useIntl,
  type MessageValue,
  type NoMessageValues,
} from 'react-intl';

import { type FlattenedOrderStatuses } from '@sg/graphql-schema';

import { useTelemetry } from '@order/Telemetry';
import { getOrderEta } from '@order/utils';

import { checkIfPendingOrder } from '../../context/OrderStatus';
import { useLastOrderStatus } from '../useLastOrderStatus';

import type { RequestPolicy } from 'urql';

// ─────────────────────────────────────────────────────────────────────────────

export const usePendingOrder = (params: Params) => {
  const { requestPolicy, withPolling = false } = params;

  const { formatMessage } = useIntl();
  const { track } = useTelemetry();

  const lastOrder = useLastOrderStatus({ requestPolicy, withPolling });

  // ─── Derived ───────────────────────────────────────────────────────────────

  const pendingOrder = useMemo(
    () => getPendingOrder({ formatMessage, lastOrder }),
    [formatMessage, lastOrder],
  );

  // ─── Handlers ──────────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    track('home.banner.cta_tapped', { destination: 'OrderStatus' });
  }, [track]);

  return useMemo(() => {
    if (!pendingOrder) return;

    return { ...pendingOrder, onPress: handlePress };
  }, [pendingOrder, handlePress]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getPendingOrder(params: GetPendingOrderParams): PendingOrder | undefined {
  const { formatMessage, lastOrder } = params;

  const { orderId, orderStatus, orderTime } = lastOrder;

  if (!orderId || !orderStatus || !orderTime) return;
  if (!checkIfPendingOrder(orderStatus)) return;

  const eta = getOrderEta({ orderTime, orderType: lastOrder.orderType });

  return {
    actionLabel: formatMessage(messages.actionLabel),
    eta,
    etaLabel: formatMessage(messages.etaLabel, { eta }),
    href: { pathname: '/order-status/[orderId]', params: { orderId } },
    orderStatus,
    statusLabel: formatMessage(messages.statusLabel, { orderStatus }),
  };
}

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly actionLabel: NoMessageValues;
  readonly etaLabel: { readonly eta: MessageValue };
  readonly statusLabel: { readonly orderStatus: string };
}>({
  actionLabel: {
    defaultMessage: 'Check status',
    description: 'Pending order | Status link label',
  },
  etaLabel: {
    defaultMessage: 'ETA {eta}',
    description: 'Pending order | ETA',
  },
  statusLabel: {
    defaultMessage:
      '{orderStatus, select, received {Order received} preparing {Preparing order} readyForCourier {Awaiting courier} delivering {Delivering order} other {Order in progress}}',
    description: 'Pending order | Status',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GetPendingOrderParams = {
  formatMessage: IntlShape['formatMessage'];
  lastOrder: ReturnType<typeof useLastOrderStatus>;
};

type PendingOrder = {
  actionLabel: string;
  eta: string;
  etaLabel: string;
  href: Href;
  orderStatus: FlattenedOrderStatuses;
  statusLabel: string;
};

type Params = {
  requestPolicy: RequestPolicy;
  withPolling?: boolean;
};
